import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AdminSubNav from "../components/AdminSubNav";
import { canManageOrg } from "../lib/permissions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_AGE_DAYS = 60;

const normalizeText = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const computeDaysRemaining = (deletedAt) => {
  if (!deletedAt) return MAX_AGE_DAYS;
  const deleted = new Date(deletedAt);
  if (Number.isNaN(deleted.getTime())) return MAX_AGE_DAYS;

  // Strict 24-hour based countdown from exact deletion time
  const now = new Date();
  const diffMs = now.getTime() - deleted.getTime();
  const diffDays = diffMs / (24 * 60 * 60 * 1000);
  const remaining = MAX_AGE_DAYS - diffDays;
  const days = Math.floor(remaining);
  return days < 0 ? 0 : days;
};

const AdminRecycleBin = ({ user, setUser }) => {
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("deleted_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const toggleSort = (field) => {
    setSortBy((prevField) => {
      if (prevField === field) {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        return prevField;
      }
      setSortOrder("asc");
      return field;
    });
  };

  const navigate = useNavigate();

  const pendingScrollRef = useRef(null);
  const scrollYRef = useRef(0);

  // Restore UI state on mount (search, sort, scroll)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("recycle_bin_ui_state");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.search === "string") setSearch(parsed.search);
        if (typeof parsed.sortBy === "string") setSortBy(parsed.sortBy);
        if (typeof parsed.sortOrder === "string") setSortOrder(parsed.sortOrder);
        if (typeof parsed.scrollY === "number" && parsed.scrollY > 0) {
          pendingScrollRef.current = parsed.scrollY;
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Execute deferred scroll restoration after items render
  useEffect(() => {
    if (pendingScrollRef.current != null && !loading && items.length > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: pendingScrollRef.current, behavior: "auto" });
        pendingScrollRef.current = null;
      });
    }
  }, [loading, items]);

  // Track scroll position continuously
  useEffect(() => {
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Persist UI state when leaving the page
  useEffect(() => {
    return () => {
      try {
        sessionStorage.setItem("recycle_bin_ui_state", JSON.stringify({
          search,
          sortBy,
          sortOrder,
          scrollY: scrollYRef.current,
        }));
      } catch (e) { /* ignore */ }
    };
  }, [search, sortBy, sortOrder]);

  const loadDeletedItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const resp = await axios.get(`${API}/admin/recycle-bin/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(resp.data || []);
      setSelectedIds([]);
    } catch (err) {
      console.error("Recycle bin load error:", err);
      toast.error("Error loading recycle bin items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeletedItems();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleRestore = async () => {
    if (!selectedIds.length) return;
    try {
      setRestoring(true);
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/recycle-bin/items/restore`,
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const restored = resp.data?.restored_ids || [];
      if (restored.length) {
        toast.success(`Restored ${restored.length} item(s)`);
      } else {
        toast.info("No items restored");
      }
      await loadDeletedItems();
    } catch (err) {
      console.error("Restore items error:", err);
      toast.error("Error restoring items");
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm("Permanently delete selected items? This cannot be undone.")) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem("access_token");
      const resp = await axios.delete(
        `${API}/admin/recycle-bin/items/permanent`,
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          data: { ids: selectedIds },
        }
      );
      const deleted = resp.data?.deleted_ids || [];
      if (deleted.length) {
        toast.success(`Permanently deleted ${deleted.length} item(s)`);
      } else {
        toast.info("No items deleted");
      }
      await loadDeletedItems();
    } catch (err) {
      console.error("Permanent delete error:", err);
      toast.error("Error permanently deleting items");
    } finally {
      setDeleting(false);
    }
  };

  const handleEmptyMyOrg = async () => {
    const myOrgItems = items.filter((it) => {
      const raw = (it.organization || "Public").trim();
      const norm = raw.toLowerCase() === "public" ? "Public" : raw.toUpperCase();
      const myRaw = (user?.organization || "Public").trim();
      const myNorm = myRaw.toLowerCase() === "public" ? "Public" : myRaw.toUpperCase();
      return norm === myNorm;
    });
    const count = myOrgItems.length;
    if (count === 0) {
      toast.info("Recycle bin is already empty for your organization");
      return;
    }
    if (!window.confirm(`Permanently delete ${count} item(s) from your organization's recycle bin? This cannot be undone.`)) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem("access_token");
      const resp = await axios.delete(
        `${API}/admin/recycle-bin/items/empty-mine`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const deleted = resp.data?.deleted_ids || [];
      toast.success(`Permanently deleted ${deleted.length} item(s) from your organization's bin`);
      await loadDeletedItems();
    } catch (err) {
      console.error("Empty org bin error:", err);
      toast.error(err?.response?.data?.detail || "Error emptying organization recycle bin");
    } finally {
      setDeleting(false);
    }
  };

  const handleEmptyAll = async () => {
    if (items.length === 0) {
      toast.info("Recycle bin is already empty");
      return;
    }
    if (!window.confirm(`Permanently delete ALL ${items.length} item(s) in the recycle bin (across every organization)? This cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? This will wipe every soft-deleted item globally.")) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem("access_token");
      const resp = await axios.delete(
        `${API}/admin/recycle-bin/items/empty-all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const deleted = resp.data?.deleted_ids || [];
      toast.success(`Permanently deleted ${deleted.length} item(s) globally`);
      await loadDeletedItems();
    } catch (err) {
      console.error("Empty entire bin error:", err);
      toast.error(err?.response?.data?.detail || "Error emptying entire recycle bin");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-6">
              <button onClick={() => navigate("/admin")} className="text-lg font-semibold">
                Admin
              </button>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="hidden sm:inline text-slate-200">{user?.email}</span>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-500 text-slate-900 bg-white hover:bg-slate-100"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="recycle-bin" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>
            Recycle Bin
          </h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!selectedIds.length || restoring}
              onClick={handleRestore}
            >
              Restore selected
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`text-red-600 border-red-300 hover:bg-red-50 ${
                !canManageOrg(user) ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={
                !selectedIds.length ||
                deleting ||
                !canManageOrg(user)
              }
              onClick={handlePermanentDelete}
              data-testid="delete-permanent-btn"
            >
              Delete permanently
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              disabled={deleting || items.length === 0}
              onClick={handleEmptyMyOrg}
              data-testid="empty-my-org-bin-btn"
              title="Permanently delete every item in your organization's recycle bin"
            >
              Empty my org&apos;s bin
            </Button>
            {canManageOrg(user) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-700 border-red-400 bg-red-50 hover:bg-red-100"
                disabled={deleting || items.length === 0}
                onClick={handleEmptyAll}
                data-testid="empty-all-bin-btn"
                title="Permanently delete every item in the recycle bin across every organization (superadmin/2RA-Edition only)"
              >
                Empty entire bin
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, category, organization..."
            className="w-72 text-xs"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">Loading deleted items...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-600">No deleted items in the recycle bin.</div>
        ) : (
          (() => {
            // Filter items based on search
            const filteredItems = items.filter((item) => {
              if (!search.trim()) return true;
              const searchTerm = normalizeText(search);
              return (
                normalizeText(item.title).includes(searchTerm) ||
                normalizeText(item.category).includes(searchTerm) ||
                normalizeText(item.organization).includes(searchTerm)
              );
            });

            const sortedItems = [...filteredItems].sort((a, b) => {
              const factor = sortOrder === "desc" ? -1 : 1;

              if (sortBy === "title") {
                const at = normalizeText(a.title);
                const bt = normalizeText(b.title);
                if (at < bt) return -1 * factor;
                if (at > bt) return 1 * factor;
                return 0;
              }

              if (sortBy === "category") {
                const ac = normalizeText(a.category);
                const bc = normalizeText(b.category);
                if (ac < bc) return -1 * factor;
                if (ac > bc) return 1 * factor;
                return 0;
              }

              if (sortBy === "organization") {
                const ao = normalizeText(a.organization || "Public");
                const bo = normalizeText(b.organization || "Public");
                if (ao < bo) return -1 * factor;
                if (ao > bo) return 1 * factor;
                return 0;
              }

              if (sortBy === "remaining") {
                const ar = computeDaysRemaining(a.deleted_at);
                const br = computeDaysRemaining(b.deleted_at);
                if (ar < br) return -1 * factor;
                if (ar > br) return 1 * factor;
                return 0;
              }

              // default: deleted_at
              const ad = new Date(a.deleted_at || "");
              const bd = new Date(b.deleted_at || "");
              const atime = Number.isNaN(ad.getTime()) ? 0 : ad.getTime();
              const btime = Number.isNaN(bd.getTime()) ? 0 : bd.getTime();
              if (atime < btime) return -1 * factor;
              if (atime > btime) return 1 * factor;
              return 0;
            });

            return sortedItems.length === 0 ? (
              <div className="text-center py-12 text-slate-600">
                No items match your search criteria.
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md">
                <div className="w-full overflow-x-auto">
                  <table className="min-w-full table-fixed">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="w-10 px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={selectedIds.length > 0 && selectedIds.length === sortedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(filteredItems.map((it) => it.id));
                              } else {
                                setSelectedIds([]);
                              }
                            }}
                          />
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => toggleSort("title")}
                        >
                          Title {sortBy === "title" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => toggleSort("category")}
                        >
                          Category {sortBy === "category" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => toggleSort("organization")}
                        >
                          Organization {sortBy === "organization" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                          Deleted by
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => toggleSort("deleted_at")}
                        >
                          Deleted at {sortBy === "deleted_at" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer select-none"
                          onClick={() => toggleSort("remaining")}
                        >
                          Remaining days {sortBy === "remaining" && (sortOrder === "asc" ? "↑" : "↓")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {sortedItems.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 align-top">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => toggleSelect(item.id)}
                            />
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="font-medium text-slate-900 truncate max-w-xs" title={item.title}>
                              {item.title}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-xs text-slate-700">
                            {item.category || "-"}
                          </td>
                          <td className="px-4 py-4 text-xs text-slate-700">
                            {item.organization || "Public"}
                          </td>
                          <td className="px-4 py-4 text-xs text-slate-700">
                            {item.deleted_by_email || "-"}
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-500">
                            {item.deleted_at || "-"}
                          </td>
                          <td className="px-4 py-4 text-xs text-slate-700">
                            {computeDaysRemaining(item.deleted_at)} days
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default AdminRecycleBin;
